import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { 
  ArrowLeft, Brain, CheckCircle2, Clock, Lightbulb, 
  MessageSquare, Target, Award, ChevronDown, ChevronUp, Save, BookOpen, Loader2
} from "lucide-react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const ModuleDetailPage = () => {
  const navigate = useNavigate();
  const { moduleId } = useParams();
  const { token } = useAuth();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [expandedConcepts, setExpandedConcepts] = useState({});
  const [reflections, setReflections] = useState({});
  const [savingReflection, setSavingReflection] = useState(null);

  useEffect(() => {
    fetchModule();
    fetchReflections();
  }, [moduleId]);

  const fetchModule = async () => {
    try {
      const response = await axios.get(`${API}/emotional-intelligence/modules/${moduleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModule(response.data);
    } catch (error) {
      console.error("Error fetching module:", error);
      if (error.response?.status === 403) {
        toast.error("Premium subscription required for this module");
        navigate("/emotional-intelligence");
      } else {
        toast.error("Failed to load module");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchReflections = async () => {
    try {
      // Extract numeric ID from moduleId (e.g., "ei-1" -> 1)
      const numericId = moduleId.replace('ei-', '');
      const response = await axios.get(`${API}/reflections/ei/${numericId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.reflections) {
        const reflectionMap = {};
        response.data.reflections.forEach(r => {
          reflectionMap[r.prompt_index] = r.content;
        });
        setReflections(reflectionMap);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error("Failed to load reflections:", error);
      }
    }
  };

  const saveReflection = async (promptIndex, prompt) => {
    const content = reflections[promptIndex];
    if (!content?.trim()) return;
    
    // Extract numeric ID from moduleId (e.g., "ei-1" -> 1)
    const numericId = parseInt(moduleId.replace('ei-', ''));
    
    setSavingReflection(promptIndex);
    try {
      await axios.post(`${API}/reflections/save`, {
        module_type: 'emotional_intelligence',
        module_id: numericId,
        prompt_index: promptIndex,
        prompt_text: prompt,
        content: content
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Reflection saved!");
    } catch (error) {
      toast.error("Failed to save reflection");
    } finally {
      setSavingReflection(null);
    }
  };

  const goToJournalWithPrompt = (prompt) => {
    // Navigate to journal with the prompt pre-filled
    navigate(`/journal?prompt=${encodeURIComponent(prompt)}`);
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const response = await axios.post(
        `${API}/emotional-intelligence/modules/${moduleId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setModule(prev => ({ ...prev, is_completed: true }));
      
      if (response.data.badge_awarded) {
        toast.success("🏆 Congratulations! You've earned the Emotional Intelligence Mastery Badge!");
      } else {
        toast.success(`Module completed! ${response.data.total_completed}/22 modules done.`);
      }
    } catch (error) {
      console.error("Error completing module:", error);
      toast.error("Failed to mark as complete");
    } finally {
      setCompleting(false);
    }
  };

  const toggleConcept = (index) => {
    setExpandedConcepts(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center">
        <div className="animate-breathe">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
            <Brain className="w-8 h-8 text-primary" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center">
        <p className="text-muted-foreground">Module not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient pb-24" data-testid="module-detail-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/emotional-intelligence")}
            className="text-muted-foreground hover:text-foreground"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            All Modules
          </Button>
          <span className="text-sm text-muted-foreground">Module {module.order} of 22</span>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <Clock className="w-4 h-4" />
            <span>{module.duration}</span>
            {module.is_completed && (
              <span className="flex items-center gap-1 text-green-600 ml-2">
                <CheckCircle2 className="w-4 h-4" />
                Completed
              </span>
            )}
          </div>
          <h1 className="font-heading text-3xl font-semibold text-foreground mb-2">
            {module.title}
          </h1>
          <p className="text-lg text-primary">
            {module.subtitle}
          </p>
        </div>

        {/* Introduction */}
        <Card className="glass-card rounded-3xl border-0 mb-6">
          <CardContent className="p-6 md:p-8">
            <p className="text-foreground leading-relaxed text-lg">
              {module.content?.introduction}
            </p>
          </CardContent>
        </Card>

        {/* Key Concepts */}
        <div className="mb-8">
          <h2 className="font-heading text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-primary" />
            Key Concepts
          </h2>
          <div className="space-y-4">
            {module.content?.key_concepts?.map((concept, index) => (
              <Card 
                key={index} 
                className="glass-card rounded-2xl border-0 cursor-pointer"
                onClick={() => toggleConcept(index)}
              >
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading font-semibold text-foreground">
                      {concept.title}
                    </h3>
                    {expandedConcepts[index] ? (
                      <ChevronUp className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                  {expandedConcepts[index] && (
                    <p className="mt-3 text-muted-foreground leading-relaxed">
                      {concept.text}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Reflection Prompts */}
        <div className="mb-8">
          <h2 className="font-heading text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-accent" />
            Reflection Prompts
          </h2>
          <div className="space-y-6">
            {module.content?.reflection_prompts?.map((prompt, index) => (
              <Card key={index} className="glass-card rounded-2xl border-0">
                <CardContent className="p-5">
                  <div className="flex gap-3 mb-4">
                    <span className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0 text-sm text-accent font-medium">
                      {index + 1}
                    </span>
                    <p className="text-foreground italic">{prompt}</p>
                  </div>
                  
                  {/* Reflection Input */}
                  <div className="ml-9">
                    <Textarea
                      placeholder="Write your reflection here..."
                      value={reflections[index] || ""}
                      onChange={(e) => setReflections(prev => ({ ...prev, [index]: e.target.value }))}
                      className="min-h-[100px] rounded-xl mb-3 bg-white/50 dark:bg-white/5"
                    />
                    <div className="flex gap-3">
                      <Button
                        size="sm"
                        onClick={() => saveReflection(index, prompt)}
                        disabled={savingReflection === index || !reflections[index]?.trim()}
                        className="rounded-lg"
                      >
                        {savingReflection === index ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-1" />
                        ) : (
                          <Save className="w-4 h-4 mr-1" />
                        )}
                        Save Reflection
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => goToJournalWithPrompt(prompt)}
                        className="rounded-lg"
                      >
                        <BookOpen className="w-4 h-4 mr-1" />
                        Expand in Journal
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Practice */}
        {module.content?.practice && (
          <div className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              Today's Practice
            </h2>
            <Card className="glass-card rounded-2xl border-0 bg-gradient-to-br from-primary/5 to-accent/5">
              <CardContent className="p-6">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-3">
                  {module.content.practice.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {module.content.practice.instructions}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Complete Button */}
        <div className="text-center pt-6">
          {module.is_completed ? (
            <div className="flex items-center justify-center gap-2 text-green-600">
              <CheckCircle2 className="w-6 h-6" />
              <span className="font-medium">You've completed this module</span>
            </div>
          ) : (
            <Button
              onClick={handleComplete}
              disabled={completing}
              className="btn-primary px-8 py-6 text-lg"
              data-testid="complete-module-btn"
            >
              {completing ? (
                "Marking Complete..."
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5 mr-2" />
                  Mark Module as Complete
                </>
              )}
            </Button>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-10 pt-6 border-t border-border/50">
          {module.order > 1 && (
            <Button
              variant="ghost"
              onClick={() => navigate(`/emotional-intelligence/ei-${module.order - 1}`)}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous Module
            </Button>
          )}
          <div className="flex-1" />
          {module.order < 22 && (
            <Button
              variant="ghost"
              onClick={() => navigate(`/emotional-intelligence/ei-${module.order + 1}`)}
              className="text-muted-foreground hover:text-foreground"
            >
              Next Module
              <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

export default ModuleDetailPage;
